import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, RotateCcw, Undo2, AlertTriangle } from "lucide-react";
import { HeaderAccent } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABEL = {
  client: "Client", visit: "Visit", staff: "Staff", family: "Family login",
  incident: "Incident", policy: "Policy", invoice: "Invoice",
  enquiry: "Enquiry", service_request: "Service request",
};

function daysLeft(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  const d = Math.ceil(ms / 86400000);
  return d <= 0 ? "expiring soon" : `${d} day${d === 1 ? "" : "s"} left`;
}

export default function Trash() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/trash").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const restore = async (t) => {
    try { await api.post(`/trash/${t.id}/restore`); toast.success("Restored"); setRows((l) => l.filter((x) => x.id !== t.id)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const purge = async (t) => {
    if (!window.confirm("Permanently delete this item? This cannot be undone.")) return;
    try { await api.delete(`/trash/${t.id}`); setRows((l) => l.filter((x) => x.id !== t.id)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const emptyAll = async () => {
    if (!window.confirm("Permanently empty the recycle bin? Everything here will be gone for good.")) return;
    try { await api.delete("/trash"); setRows([]); toast.success("Recycle bin emptied"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Recycle bin</h1>
          <p className="mt-1 text-ink-soft">Deleted items are kept here for 7 days so you can undo a mistake, then removed automatically.</p>
          <HeaderAccent />
        </div>
        {isAdmin && rows.length > 0 && (
          <button onClick={emptyAll} data-testid="empty-trash-button"
            className="inline-flex items-center gap-2 rounded-lg border border-coral px-4 py-2.5 text-sm font-semibold text-coral transition-colors hover:bg-coral hover:text-white">
            <Trash2 size={16} /> Empty bin
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
        {loading && <p className="p-8 text-center text-ink-muted">Loading…</p>}
        {!loading && rows.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-12 text-center">
            <Undo2 size={28} className="text-ink-muted" />
            <p className="text-ink-muted">The recycle bin is empty. Anything you delete will appear here for 7 days.</p>
          </div>
        )}
        {rows.map((t) => (
          <div key={t.id} data-testid={`trash-row-${t.id}`} className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3.5 last:border-0">
            <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-soft">{TYPE_LABEL[t.entity_type] || t.entity_type}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">{t.label}</p>
              <p className="text-xs text-ink-muted">Deleted by {t.deleted_by_name} · {new Date(t.deleted_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
            </div>
            <span className="flex items-center gap-1 text-xs font-medium text-gold-dark"><AlertTriangle size={13} /> {daysLeft(t.expires_at)}</span>
            <button onClick={() => restore(t)} data-testid={`restore-${t.id}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-sage px-3 py-1.5 text-xs font-semibold text-white hover:brightness-105">
              <RotateCcw size={13} /> Restore
            </button>
            {isAdmin && (
              <button onClick={() => purge(t)} data-testid={`purge-${t.id}`} title="Delete permanently"
                className="rounded-lg border border-border p-1.5 text-coral hover:bg-coral-light">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
